import { create } from "zustand";
import type { ChatErrorMessage } from "@lexiun/lx-llm-errors";
import type { ChatMessage } from "@/types/gateway";
import type { ContentBlock, ToolUseBlock as ToolUseBlockT } from "@/types/content-blocks";
import { isPlanTool } from "@/lib/chat/tool-activity";
import { chatMessageKey } from "@/lib/chat/lx-history";

/**
 * The live work state of a single turn — what the in-flow header says, and how
 * long the concierge worked before it began composing.
 *
 * ## The freeze rule (miniplan §3, amendment requested of the spec)
 *
 * `workedMs` freezes at the LATEST answer-start, not the first. When a tool
 * fires mid-answer the header flips back to the activity label and the number
 * is re-measured when composition resumes.
 *
 * That is not a stylistic choice. L2 persists `workedMs` as run-start → the
 * LAST `model_call_started`, and the runtime lane's ruling rests on that erring
 * EARLY so the persisted figure stays ≤ the live-frozen one. Freezing at the
 * FIRST answer-start would make the live number smaller than the persisted one
 * on any turn with a mid-answer tool — the reload would then show a LARGER
 * duration than the lawyer watched freeze, which is the exact failure the
 * omit-never-substitute rule exists to prevent.
 */
export interface TurnActivity {
  /** Epoch ms the run began. `null` = no run. */
  startedAt: number | null;
  /**
   * Did THIS client witness the run's START?
   *
   * `false` for a run adopted mid-flight (`beginServerTurn`) — an IM-originated
   * turn, say, which may already have been working for a minute when the socket
   * first tells us it exists. `startedAt` is then the moment we began WATCHING:
   * the honest input for the live clock, and a fabrication for a duration.
   *
   * ⚠ So `workedMs` freezes only when this is `true`. The runtime writes the
   * real figure onto the persisted row and has since 2026-08-13
   * (`ai-docs/lexiun-conversation-history-plan.md` §6.4-bis rule 3), so
   * measuring from our own arrival did not fill a gap — it OVERWROTE a true
   * value with an invented one, and rendered "Trabalhou por 1s" on a 77 s
   * Telegram turn (ui-tester `I-rt1-20260911-1811-localdev-11`; `formatWorked`'s
   * `Math.max(1, …)` floor is what made it exactly 1 s rather than 0).
   */
  startObserved: boolean;
  /** The most recent tool step, for the status line. `null` = idle
   *  ("Pensando…"), which is also the state before the first tool fires. */
  latest: { name: string; target?: string } | null;
  /** Frozen elapsed run-start → latest answer-start.
   *  ⚠ `null` = NEVER OBSERVED ⇒ render no duration. Not zero. */
  workedMs: number | null;
  /** Per-invocation outcome marks, keyed by `toolCallId`. Populated from
   *  `phase:"result"`'s `isError`, which survives the server-side payload
   *  strip — so a mark is measured, never inferred. */
  outcomes: Record<string, "ok" | "error">;
  /** `true` while the final answer is composing (no tool since the last text
   *  growth). Drives header text: activity label when false, frozen duration
   *  when true. */
  answering: boolean;
  /** Total streamed text length observed when the most recent tool fired.
   *  Re-freezing waits for text to grow PAST this — the wire sends cumulative
   *  snapshots, so "a delta arrived" is not by itself proof of new text. */
  textLenAtLastTool: number;
}

const IDLE_ACTIVITY: TurnActivity = Object.freeze({
  startedAt: null,
  startObserved: false,
  latest: null,
  workedMs: null,
  outcomes: {},
  answering: false,
  textLenAtLastTool: 0,
}) as TurnActivity;

/** `startObserved` is REQUIRED rather than defaulted: a caller that adopts a
 *  run in progress must say so, and a silent default would make the wrong
 *  answer the quiet one. */
function startedActivity(now: number, startObserved: boolean): TurnActivity {
  return {
    startedAt: now,
    startObserved,
    latest: null,
    workedMs: null,
    outcomes: {},
    answering: false,
    textLenAtLastTool: 0,
  };
}

/** Total length of the text blocks in a streaming snapshot. */
function textLengthOf(blocks: ContentBlock[]): number {
  let n = 0;
  for (const b of blocks) if (b.type === "text") n += b.text.length;
  return n;
}

interface SessionChatState {
  messages: ChatMessage[];
  /** Mid-flight assistant content as typed blocks. Replaces the legacy
   *  `streamingText: string`. Each chat-event delta REPLACES this with
   *  the latest cumulative snapshot (today's wire shape sends full
   *  snapshots, not per-block deltas — see plan §2 "Open implementation
   *  question — streaming protocol"). When the run finalizes,
   *  `finalizeRun` moves these into the assistant message and clears. */
  streamingBlocks: ContentBlock[];
  /** Live tool blocks bridged from `session.tool` EventFrames (the chat wire
   *  itself is TEXT-ONLY — frozen core server-chat.ts emits only
   *  `[{type:"text"}]` in delta AND final payloads, so tool_use blocks can
   *  NEVER arrive on the "chat" event; see
   *  ai-docs/handoffs/lexide-todo-card-not-rendering-handoff-2026-07-18.md).
   *
   *  ⚠ Two statements that used to live here are now WRONG and are corrected
   *  rather than left to mislead: this is no longer plan-tools-only (L1 widened
   *  it to the D1 curated set, `deddc74b8`), and it is no longer upsert-by-NAME
   *  (each invocation appends its own row since the 2026-08-13 operator
   *  directive — see `upsertLiveToolBlock`). Plan tools remain the one
   *  name-keyed exception, which is the part of the old sentence that survived. */
  liveToolBlocks: ToolUseBlockT[];
  /** Live work state of the turn in flight (L0+L1). Always present; a session
   *  with no run carries the idle singleton. */
  activity: TurnActivity;
  activeRunId: string | null;
  isLoading: boolean;
  /** A classified `ChatErrorMessage` envelope when the failure was
   *  classifiable (the common case), or a raw string for legacy/unclassified
   *  failures. The render layer dispatches on the shape. */
  error: string | ChatErrorMessage | null;
  /**
   * `true` once the initial `chat.history` load for this session has
   * settled (any outcome — messages, empty, or failure). Lets a surface
   * defer its empty-vs-populated decision until history is known, so the
   * global-home welcome hero doesn't flash before the transcript
   * hydrates on reload. Sticky once set.
   */
  historyLoaded: boolean;
  /** Cursor of the OLDEST loaded block (`lx.chat_history` paged contract,
   *  agent-bus P6 item 2). Pass as `before` to fetch the preceding block.
   *  `null` = no cursor known (full read, empty thread, or pre-paging
   *  runtime). */
  historyCursor: string | null;
  /** Whether older messages exist before the oldest loaded block. MEASURED
   *  server-side — never inferred from page fullness. */
  hasMoreHistory: boolean;
  /** THIRD STATE of the initial history read: the read FAILED. Distinct from
   *  `historyLoaded` + empty `messages`, which genuinely means "no history".
   *  Rendering this state as an empty thread shows a lawyer an empty case
   *  that is not empty — the surface must say "could not load" instead. */
  historyReadError: string | null;
  /** A back-scroll page fetch is in flight (guards double-fires from the
   *  top sentinel). */
  loadingOlder: boolean;
  /** A back-scroll page fetch FAILED (e.g. INVALID_PARAMS on the cursor).
   *  Rendered as an inline "could not load older messages" row — never
   *  auto-retried bare. */
  olderError: string | null;
}

/** Stable singleton for sessions that don't exist yet — avoids re-render churn from new object references */
const EMPTY_SESSION: SessionChatState = Object.freeze({
  messages: [] as ChatMessage[],
  streamingBlocks: [] as ContentBlock[],
  liveToolBlocks: [] as ToolUseBlockT[],
  activity: IDLE_ACTIVITY,
  activeRunId: null,
  isLoading: false,
  error: null,
  historyLoaded: false,
  historyCursor: null,
  hasMoreHistory: false,
  historyReadError: null,
  loadingOlder: false,
  olderError: null,
}) as SessionChatState;

function emptySession(): SessionChatState {
  return {
    messages: [],
    streamingBlocks: [],
    liveToolBlocks: [],
    activity: IDLE_ACTIVITY,
    activeRunId: null,
    isLoading: false,
    error: null,
    historyLoaded: false,
    historyCursor: null,
    hasMoreHistory: false,
    historyReadError: null,
    loadingOlder: false,
    olderError: null,
  };
}

interface ChatStoreState {
  /** Per-session chat state keyed by sessionKey */
  sessions: Map<string, SessionChatState>;

  getSession: (sessionKey: string) => SessionChatState;
  /** Append an optimistic user-turn to the session. `selection`, when
   *  supplied, is the viewer text-selection the lawyer had active when
   *  they hit send — gets stamped onto the message so the bubble's
   *  echo sub-line renders IMMEDIATELY (no in-memory FIFO bridge
   *  needed). On reload, the same `selection` field is hydrated from
   *  the server-persisted `LxConversationMessage.context` so the echo
   *  survives across page lifetimes. See plan §11 + handoff
   *  `lexide-retire-sent-selection-store-handoff-2026-06-29.md`. */
  addUserMessage: (
    sessionKey: string,
    content: string,
    runId: string,
    selection?: { source: string; text: string },
    mentions?: { path: string; name: string }[],
    voice?: { audioUrl?: string },
  ) => void;
  /** Set the cumulative streaming blocks for an in-flight run. Today's
   *  wire protocol sends snapshots (each delta replaces the prior
   *  blocks); when per-block deltas land we'll add `appendStreamingBlock`. */
  setStreamingBlocks: (sessionKey: string, runId: string, blocks: ContentBlock[]) => void;
  /**
   * Upsert a live tool block bridged from a `session.tool` frame.
   *
   * ## Keyed by INVOCATION, not by tool name (operator directive 2026-08-13)
   *
   * Each call APPENDS its own row, so the lawyer watches a history accumulate
   * rather than one line mutating. Operator, verbatim: *"the idea is to display
   * them in scrolled form so the user can see the history of all previous tasks
   * already run."* Before this, a turn with ten `lx_read`s showed one row.
   *
   * **EXCEPTION — plan tools stay NAME-keyed.** `lx_todo`/`update_plan` are one
   * evolving TodoCard by design; keying them per invocation would stack N
   * snapshots of the same list.
   *
   * ⚠ A frame with no `toolCallId` falls back to a `name:runId` id, which is
   * stable per tool per run and therefore COALESCES. That is deliberate: if the
   * wire cannot distinguish two invocations, rendering two rows invents a
   * distinction the data does not carry, and a re-delivered frame would
   * duplicate. The live wire does send `toolCallId` (measured), so this is the
   * rare path.
   *
   * Ignored when the session has no active run (frames for turns this client
   * didn't initiate — e.g. IM turns — must not leave a dangling card that never
   * finalizes).
   */
  upsertLiveToolBlock: (
    sessionKey: string,
    block: ToolUseBlockT,
    opts?: { requireActiveRun?: boolean },
  ) => void;
  /** A curated tool STARTED — becomes the status line and flips the header
   *  back to "working" (re-arming the freeze for the composition that
   *  follows). `target` is the already-derived, already-truncated label arg. */
  noteToolStart: (
    sessionKey: string,
    step: { name: string; target?: string },
  ) => void;
  /** A curated tool FINISHED — records the measured outcome for its live
   *  block's completion mark. `isError` comes off the wire; nothing here
   *  guesses. */
  noteToolResult: (sessionKey: string, callId: string, isError: boolean) => void;
  /** Finalize a run — moves `streamingBlocks` (or the provided override)
   *  into a new assistant message in `messages`, clears the streaming
   *  state. */
  finalizeRun: (
    sessionKey: string,
    runId: string,
    blocks: ContentBlock[],
    /** Producing model/provider from the frame. Stamped onto the message so
     *  the renderer can tell a runtime-injected receipt from a turn the
     *  concierge actually composed (`lib/chat-receipt.ts`). */
    meta?: { model?: string; provider?: string },
  ) => void;
  /**
   * F3 (miniplan §6) — enter the ordinary prompt-submission progress state for
   * a turn the SERVER initiated, without inventing a user message to hang it
   * on. Used when an upload arms a submission-reaction turn: the lawyer typed
   * nothing, but the reaction must present exactly like a reply to a prompt.
   *
   * Sets the same fields `addUserMessage` sets minus the message itself, so
   * every downstream consumer (composer disabled state, streaming cursor,
   * `setStreamingBlocks`' run guard) behaves identically to a typed turn.
   *
   * No-ops when a run is ALREADY active: the lawyer's own in-flight turn owns
   * the progress state, and stealing `activeRunId` from it would strand their
   * reply — its deltas would start being dropped as another run's.
   */
  beginServerTurn: (sessionKey: string, runId: string) => void;
  /**
   * Clear a progress state armed by `beginServerTurn` that never produced a
   * turn, without disturbing anything else. Safe by construction: it only
   * clears when `activeRunId` still equals `runId`, so a real turn that
   * started in the meantime is never cancelled.
   */
  abandonServerTurn: (sessionKey: string, runId: string) => void;
  /**
   * ⚖ Clear a run the SERVER has already finished — reconciliation, not a
   * timeout and not an abort.
   *
   * Distinct from `abandonServerTurn` (a turn that never produced anything)
   * and from `abortRun` (the lawyer stopped it, which fabricates an assistant
   * row from whatever was observed). This one fires on POSITIVE EVIDENCE: the
   * persisted history already carries the finalized assistant row for `runId`,
   * so the turn is demonstrably over and the local progress state is stale.
   *
   * Fabricates nothing. The row itself arrives through the ordinary tail-history
   * merge; this only releases the lock the missing `final` frame left behind.
   * Guarded on `activeRunId === runId` so a newer turn is never cancelled.
   */
  reconcileEndedRun: (sessionKey: string, runId: string) => void;
  /**
   * ⚖ A-11 turn-end hydration — merge per-step provenance (from the persisted
   * v>=3 row) into the ALREADY-finalized assistant message of `runId`.
   *
   * Matches `(runId, role: "assistant")` only — the user row shares the runId
   * and touching it would hang provenance on the lawyer's own message (the
   * same per-(runId, role) rule finalizeRun's unsolicited branch obeys).
   * No-ops when the message does not exist (the turn was cleared or never
   * finalized) — hydration never creates state, only annotates it.
   */
  hydrateTurnAttribution: (
    sessionKey: string,
    runId: string,
    attribution: Record<string, { provider: string; path?: string }>,
  ) => void;
  setError: (sessionKey: string, error: string | ChatErrorMessage) => void;
  abortRun: (sessionKey: string, blocks?: ContentBlock[]) => void;
  setHistory: (sessionKey: string, messages: ChatMessage[]) => void;
  setLoading: (sessionKey: string, loading: boolean) => void;
  /** Mark the initial history load for a session as settled (sticky). */
  markHistoryLoaded: (sessionKey: string) => void;
  /** Record the paged-read metadata of the OLDEST loaded block (P6 item 2).
   *  Called by the initial paged load and by every back-scroll page. */
  setHistoryPage: (
    sessionKey: string,
    meta: { cursor?: string; hasMore: boolean },
  ) => void;
  /** Record (or clear) the initial read's THIRD state — "we could not look".
   *  Never conflated with an empty thread. */
  setHistoryReadError: (sessionKey: string, readError: string | null) => void;
  /** Guard + error state for the back-scroll fetch. */
  setLoadingOlder: (sessionKey: string, loading: boolean) => void;
  setOlderError: (sessionKey: string, error: string | null) => void;
  /**
   * Prepend an OLDER history block (back-scroll pagination). Rows whose
   * `(runId, role)` identity already exists in the session are dropped — a
   * page overlapping the loaded window must not duplicate into scrollback.
   * Also advances the cursor/hasMore metadata to the new oldest block.
   */
  prependHistory: (
    sessionKey: string,
    older: ChatMessage[],
    meta: { cursor?: string; hasMore: boolean },
  ) => void;
  /**
   * Append tail rows the poll found that the live wire did not deliver
   * (P6 item 2 tail polling — ⚖ D-8 turns land in threads nobody is
   * watching). Rows already present by `(runId, role)` are dropped, so a
   * poll racing the WS final is a no-op. Returns the number of rows added.
   */
  appendTailHistory: (sessionKey: string, rows: ChatMessage[]) => number;
  clearSession: (sessionKey: string) => void;
}

function updateSession(
  sessions: Map<string, SessionChatState>,
  sessionKey: string,
  updater: (s: SessionChatState) => SessionChatState,
): Map<string, SessionChatState> {
  const next = new Map(sessions);
  const current = next.get(sessionKey) ?? emptySession();
  next.set(sessionKey, updater(current));
  return next;
}

/**
 * Merge a freshly-arrived `chat.history` payload with whatever the
 * store has accumulated locally (the most common case: ONE optimistic
 * user message that the lawyer typed before history resolved). Exported
 * so the unit test can pin the race-protection behaviour without
 * driving the whole store.
 *
 * Rules:
 *   1. If there are no locals, history wins outright.
 *   2. If there is no history (empty session OR a brand-new session
 *      that never persisted), locals win outright.
 *   3. Otherwise: history is canonical for everything up to and
 *      including the newest history timestamp. Any local message
 *      strictly NEWER than that boundary is appended (these are
 *      optimistic just-typed entries that the server didn't have
 *      yet when the read happened). Local messages older than the
 *      boundary are dropped — they're a stale local view; history
 *      is the canonical past.
 *
 * Clock-skew tolerance: timestamps come from JS Date.now() (locals)
 * vs server's persisted ISO/epoch strings (history). Within a few
 * seconds the comparison is safe; in pathological clock-skew the
 * worst case is a recently-sent local message being treated as
 * "past" and dropped — not history loss, which is the bug we're
 * actually fixing.
 */
export function mergeHistoryWithLocal(
  history: ChatMessage[],
  local: ChatMessage[],
): ChatMessage[] {
  if (local.length === 0) return history;
  if (history.length === 0) return local;
  const newestHistoryTs = Math.max(
    ...history.map((m) => parseChatTimestamp(m.timestamp)),
  );
  const futureLocals = local.filter(
    (m) => parseChatTimestamp(m.timestamp) > newestHistoryTs,
  );
  return [...history, ...futureLocals];
}

function parseChatTimestamp(ts: string): number {
  const n = Date.parse(ts);
  return Number.isFinite(n) ? n : 0;
}

export const useChatStore = create<ChatStoreState>((set, get) => ({
  sessions: new Map(),

  getSession: (sessionKey) => get().sessions.get(sessionKey) ?? EMPTY_SESSION,

  addUserMessage: (sessionKey, content, runId, selection, mentions, voice) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => ({
        ...ss,
        messages: [
          ...ss.messages,
          {
            role: "user" as const,
            content: [{ type: "text", text: content }],
            timestamp: new Date().toISOString(),
            runId,
            // Stamp selection only when present — `undefined` (the
            // common no-selection case) is intentionally NOT spread so
            // the message shape mirrors what server-history fetches
            // emit for selectionless turns. R2 in the handoff: prior
            // turn's selection must NOT leak; each addUserMessage call
            // decides independently.
            ...(selection ? { selection } : {}),
            // Same gate for @-mention references — present only on the
            // turn they were attached (live echo; not server-persisted).
            ...(mentions && mentions.length > 0 ? { mentions } : {}),
            // Voice-dictated turn (auto-submitted mic recording) — the
            // bubble renders the voice-note header; `audioUrl` is a
            // session-local object URL for immediate playback.
            ...(voice ? { voice } : {}),
          },
        ],
        activeRunId: runId,
        activity: startedActivity(Date.now(), true),
        isLoading: true,
        error: null,
      })),
    })),

  setStreamingBlocks: (sessionKey, runId, blocks) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => {
        if (ss.activeRunId !== runId) return ss;
        // ── the FREEZE half of the work header ──────────────────────────────
        // Answer-start = the text growing past where it stood when the most
        // recent tool fired. Comparing LENGTH rather than reacting to the
        // delta itself is what makes this correct on a cumulative wire: a
        // snapshot re-sent unchanged is not new composition.
        const a = ss.activity;
        const len = textLengthOf(blocks);
        const activity =
          a.startedAt !== null && !a.answering && len > a.textLenAtLastTool
            ? {
                ...a,
                // `answering` still flips: it is what hides the live working
                // cue when the answer starts, and that is correct for an
                // adopted run too.
                answering: true,
                // The DURATION is what must not be invented. Frozen only when
                // we saw the run start; otherwise omitted, and the renderer
                // shows no duration at all (never "0 s", never a substitute —
                // `lexiun-conversation-history-plan.md` §6.4-bis rule 3), while
                // the persisted row carries the runtime's true figure.
                ...(a.startObserved ? { workedMs: Date.now() - a.startedAt } : {}),
              }
            : a;
        return { ...ss, streamingBlocks: blocks, activity, isLoading: false };
      }),
    })),

  noteToolStart: (sessionKey, step) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => {
        if (!ss.activeRunId) return ss;
        // Flip BACK to working and re-arm the freeze against the text as it
        // stands NOW, so the next composition is measured from this step —
        // not from a first token that a later tool has already superseded.
        return {
          ...ss,
          activity: {
            ...ss.activity,
            latest: step,
            answering: false,
            textLenAtLastTool: textLengthOf(ss.streamingBlocks),
          },
        };
      }),
    })),

  noteToolResult: (sessionKey, callId, isError) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => {
        if (!ss.activeRunId) return ss;
        return {
          ...ss,
          activity: {
            ...ss.activity,
            outcomes: { ...ss.activity.outcomes, [callId]: isError ? "error" : "ok" },
          },
        };
      }),
    })),

  upsertLiveToolBlock: (sessionKey, block, opts) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => {
        // Mission progress outlives the delegating turn (async) — its card
        // may update with no active run (requireActiveRun:false).
        if ((opts?.requireActiveRun ?? true) && !ss.activeRunId) return ss;
        // Plan tools mutate in place; everything else appends per invocation.
        const i = isPlanTool(block.name)
          ? ss.liveToolBlocks.findIndex((b) => b.name === block.name)
          : ss.liveToolBlocks.findIndex((b) => b.id === block.id);
        const liveToolBlocks =
          i >= 0
            ? ss.liveToolBlocks.map((b, j) => (j === i ? block : b))
            : [...ss.liveToolBlocks, block];
        return { ...ss, liveToolBlocks };
      }),
    })),

  finalizeRun: (sessionKey, runId, blocks, meta) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => {
        if (ss.activeRunId !== runId) {
          // UNSOLICITED final — a server-PUSHED line for a run this client
          // never streamed. Ingest receipts are exactly this shape
          // (`runId: "lexiun-ingest:<ts>"`, no preceding delta), and so is
          // any future deterministic line the runtime emits on its own.
          //
          // This branch used to be a bare `return ss`, which dropped every
          // such frame SILENTLY. Live-caught 2026-08-03: 10 ingest receipts
          // were durably written and none rendered until a reload — the frame
          // could arrive perfectly and still show nothing, so no amount of
          // fixing the EMIT side could have made them appear.
          //
          // Deliberately does NOT touch streamingBlocks / activeRunId /
          // isLoading: a receipt that lands while the lawyer is mid-turn must
          // not cancel their in-flight run.
          if (blocks.length === 0) return ss;
          // A non-empty final for a runId that ALREADY has a message is a
          // LATER SEGMENT of a compaction-split turn (or a re-delivery) — and
          // dropping it was this store's copy of the runtime's per-run dedupe
          // bug (wedge triage §7). The honest rule mirrors their fix: the
          // answer REPLACES the placeholder content, keeping the leading tool
          // trail the earlier segment folded in. Idempotent under re-delivery:
          // replacing with the same content is a no-op in substance.
          // ⚠ Per-(runId, ROLE) — the user row shares the runId, and matching
          // it here would graft the answer onto the lawyer's own message. The
          // runtime's fix was role-scoped for the same reason.
          const existingIdx = ss.messages.findIndex(
            (m) => m.runId === runId && m.role === "assistant",
          );
          // ⚖ A merged row must carry the arrival's OWN time and place
          // (ui-tester I-7, run ui-20260903-2241).
          //
          // The merge itself is right and stays: a compaction split leaves a
          // segment under this runId and the later answer REPLACES it (see the
          // note below). What was wrong is that the replacement inherited the
          // ORIGINAL row's timestamp and position. A D-8 failure narration
          // therefore surfaced ABOVE the answers it followed, stamped 01:46 for
          // an 01:54 event, leaving "Ele retomou o planejamento" as the last
          // line of a thread whose work had already died.
          //
          // ⇒ When the content actually CHANGES, the row is the newest thing
          // said: re-stamp it and move it to the end. When the content is
          // identical it is a re-delivery, and re-delivery must stay a no-op —
          // moving it would reorder the thread on a duplicate frame.
          if (existingIdx >= 0) {
            const existing = ss.messages[existingIdx];
            let lead = 0;
            while (lead < existing.content.length && existing.content[lead].type === "tool_use") {
              lead++;
            }
            const nextContent = [...existing.content.slice(0, lead), ...blocks];
            // Re-delivery check, by CONTENT: an identical frame must change
            // nothing at all — not the row, not its stamp, not its position.
            const unchanged =
              JSON.stringify(existing.content) === JSON.stringify(nextContent);
            if (unchanged) return ss;
            const merged = {
              ...existing,
              content: nextContent,
              // The arrival's own time, not the slot's. See the I-7 note above.
              timestamp: new Date().toISOString(),
              ...(meta?.model ? { model: meta.model } : {}),
              ...(meta?.provider ? { provider: meta.provider } : {}),
            };
            // …and its own place: the newest thing said belongs at the end.
            const withoutExisting = ss.messages.filter((_, i) => i !== existingIdx);
            return { ...ss, messages: [...withoutExisting, merged] };
          }
          return {
            ...ss,
            messages: [
              ...ss.messages,
              {
                role: "assistant" as const,
                content: blocks,
                timestamp: new Date().toISOString(),
                runId,
                ...(meta?.model ? { model: meta.model } : {}),
                ...(meta?.provider ? { provider: meta.provider } : {}),
              },
            ],
          };
        }
        // ── Compaction-split turns (wedge triage §7 + §8.1, 2026-08-14) ──
        // A mid-turn compaction splits ONE user turn into SEVERAL agent runs
        // sharing one runId, and the wire emits a chat `final` at the end of
        // EVERY segment — with an EMPTY answer on all but the last (measured on
        // the 205-call specimen: run_end final_response="" twice, then the real
        // 7.8KB answer). Treating segment finals as THE final ended the live
        // turn at segment 1: activeRunId dropped, chips froze, and the real
        // answer later hit the unsolicited branch's per-run dedupe — the exact
        // bug the runtime fixed the same day in appendTurn (per-run → per-
        // (run,role)), one layer up. The lawyer watched the most expensive
        // turns "never finish" while the answer sat discarded.
        //
        // ⇒ An empty final with no streamed prose is a SEGMENT BOUNDARY, not an
        // answer: keep the run live and let the deltas keep flowing. A
        // pathological genuinely-empty final leaves the turn on the stall-
        // release affordance rather than silently swallowing a later answer —
        // the recoverable failure, not the invisible one.
        const streamedProse = ss.streamingBlocks.some(
          (b) => b.type === "text" && b.text.trim().length > 0,
        );
        if (blocks.length === 0 && !streamedProse) {
          return ss;
        }
        // Use the explicit blocks arg when provided + non-empty;
        // otherwise fall back to whatever was streaming. The wire's
        // `final` event tends to carry the full cumulative content, so
        // the arg is the authoritative source.
        const finalBlocks = [
          ...ss.liveToolBlocks,
          ...(blocks.length > 0 ? blocks : ss.streamingBlocks),
        ];
        return {
          ...ss,
          messages: [
            ...ss.messages,
            {
              role: "assistant" as const,
              content: finalBlocks,
              timestamp: new Date().toISOString(),
              runId,
              ...(meta?.model ? { model: meta.model } : {}),
              ...(meta?.provider ? { provider: meta.provider } : {}),
              // ⚠ Spread only when OBSERVED. A turn that never reached an
              // answer-start carries no duration at all — the renderer then
              // shows no header, rather than "0 s" or a run-end substitute.
              ...(ss.activity.workedMs !== null ? { workedMs: ss.activity.workedMs } : {}),
              // Measured per-step outcomes survive finalize. Without this the
              // activity reset erased an observed ✓ the moment the answer
              // landed — the operator watched "✓ Ajuda" flip to "· Ajuda" as
              // the turn completed, while a reload showed ✓ again (from the
              // persisted trail). An observation, once made, is not unmade by
              // the turn ending.
              ...(Object.keys(ss.activity.outcomes).length
                ? { outcomes: ss.activity.outcomes }
                : {}),
            },
          ],
          streamingBlocks: [],
          liveToolBlocks: [],
          activity: IDLE_ACTIVITY,
          activeRunId: null,
          isLoading: false,
        };
      }),
    })),

  hydrateTurnAttribution: (sessionKey, runId, attribution) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => {
        // Empty attribution = the matcher found nothing to say — leave the
        // message untouched rather than stamping an empty record (absence
        // must stay absent; an empty object reads as "hydrated, none found"
        // when nothing was hydrated at all).
        if (Object.keys(attribution).length === 0) return ss;
        const idx = ss.messages.findIndex(
          (m) => m.runId === runId && m.role === "assistant",
        );
        if (idx < 0) return ss;
        return {
          ...ss,
          messages: ss.messages.map((m, i) =>
            i === idx ? { ...m, trailAttribution: attribution } : m,
          ),
        };
      }),
    })),

  beginServerTurn: (sessionKey, runId) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => {
        if (ss.activeRunId) return ss;
        return {
          ...ss,
          activeRunId: runId,
          // `false`: this client is ADOPTING a run already in progress. The
          // timestamp is our arrival, not the run's start.
          activity: startedActivity(Date.now(), false),
          isLoading: true,
          error: null,
        };
      }),
    })),

  abandonServerTurn: (sessionKey, runId) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => {
        if (ss.activeRunId !== runId) return ss;
        return {
          ...ss,
          activeRunId: null,
          activity: IDLE_ACTIVITY,
          isLoading: false,
          streamingBlocks: [],
        };
      }),
    })),

  reconcileEndedRun: (sessionKey, runId) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => {
        // Same guard as abandonServerTurn: only the run we were told about.
        if (ss.activeRunId !== runId) return ss;
        return {
          ...ss,
          activeRunId: null,
          activity: IDLE_ACTIVITY,
          isLoading: false,
          // Drop the half-streamed buffer: the authoritative content is the
          // persisted row the caller is about to merge. Keeping it would
          // render the answer twice.
          streamingBlocks: [],
          liveToolBlocks: [],
        };
      }),
    })),

  setError: (sessionKey, error) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => ({
        ...ss,
        error,
        streamingBlocks: [],
        liveToolBlocks: [],
        // The header goes with the run: a turn that failed produced no answer,
        // so leaving a "Trabalhou por …" behind would attribute work to an
        // answer that does not exist.
        activity: IDLE_ACTIVITY,
        activeRunId: null,
        isLoading: false,
      })),
    })),

  abortRun: (sessionKey, blocks) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => {
        const abortedBlocks = [...ss.liveToolBlocks, ...(blocks ?? [])];
        const msgs =
          abortedBlocks.length > 0
            ? [
                ...ss.messages,
                {
                  role: "assistant" as const,
                  content: abortedBlocks,
                  timestamp: new Date().toISOString(),
                  runId: ss.activeRunId ?? undefined,
                  // Same observed-only rule as finalizeRun. An abort BEFORE
                  // any composition leaves this absent, so the trail renders
                  // with no duration — which is the honest reading: we know
                  // what it did, not how long it worked before answering,
                  // because it never began to answer.
                  ...(ss.activity.workedMs !== null ? { workedMs: ss.activity.workedMs } : {}),
                  // Outcomes observed before the abort are still observations —
                  // same rule as the normal finalize above.
                  ...(Object.keys(ss.activity.outcomes).length
                    ? { outcomes: ss.activity.outcomes }
                    : {}),
                },
              ]
            : ss.messages;
        return {
          ...ss,
          messages: msgs,
          streamingBlocks: [],
          liveToolBlocks: [],
          activity: IDLE_ACTIVITY,
          activeRunId: null,
          isLoading: false,
        };
      }),
    })),

  setHistory: (sessionKey, messages) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => {
        // History is canonical for the persisted past. Merge instead of
        // skip so the race "user types before history loads" doesn't
        // silently drop the full transcript.
        //
        // Race recap: useChatSession dispatches chat.history async on
        // mount; if the user sends BEFORE the response arrives,
        // `addUserMessage` writes [userMsg] locally → length=1. The old
        // implementation then `return ss`-ed and the user lost every
        // prior turn until a clean reload-without-typing. The new shape
        // appends any LOCAL messages that are newer than the newest
        // history entry (their optimistic just-now writes) and trusts
        // history for the canonical past. See
        // mergeHistoryWithLocal for the boundary semantics.
        const merged = mergeHistoryWithLocal(messages, ss.messages);
        return { ...ss, messages: merged };
      }),
    })),

  setLoading: (sessionKey, loading) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => ({
        ...ss,
        isLoading: loading,
      })),
    })),

  markHistoryLoaded: (sessionKey) =>
    set((s) => {
      const current = s.sessions.get(sessionKey);
      if (current?.historyLoaded) return s; // already settled — no churn
      return {
        sessions: updateSession(s.sessions, sessionKey, (ss) => ({
          ...ss,
          historyLoaded: true,
        })),
      };
    }),

  setHistoryPage: (sessionKey, meta) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => ({
        ...ss,
        historyCursor: meta.cursor ?? null,
        hasMoreHistory: meta.hasMore,
      })),
    })),

  setHistoryReadError: (sessionKey, readError) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => ({
        ...ss,
        historyReadError: readError,
      })),
    })),

  setLoadingOlder: (sessionKey, loading) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => ({
        ...ss,
        loadingOlder: loading,
      })),
    })),

  setOlderError: (sessionKey, error) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => ({
        ...ss,
        olderError: error,
      })),
    })),

  prependHistory: (sessionKey, older, meta) =>
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => {
        const present = new Set(ss.messages.map(chatMessageKey));
        const fresh = older.filter((m) => !present.has(chatMessageKey(m)));
        return {
          ...ss,
          messages: fresh.length ? [...fresh, ...ss.messages] : ss.messages,
          // The metadata always advances even when every row deduped away —
          // it describes the OLDEST block the server returned, and standing
          // still would refetch the same page forever.
          historyCursor: meta.cursor ?? null,
          hasMoreHistory: meta.hasMore,
          loadingOlder: false,
          olderError: null,
        };
      }),
    })),

  appendTailHistory: (sessionKey, rows) => {
    let added = 0;
    set((s) => ({
      sessions: updateSession(s.sessions, sessionKey, (ss) => {
        const present = new Set(ss.messages.map(chatMessageKey));
        const fresh = rows.filter((m) => !present.has(chatMessageKey(m)));
        added = fresh.length;
        if (!fresh.length) return ss;
        return { ...ss, messages: [...ss.messages, ...fresh] };
      }),
    }));
    return added;
  },

  clearSession: (sessionKey) =>
    set((s) => {
      const next = new Map(s.sessions);
      next.delete(sessionKey);
      return { sessions: next };
    }),
}));
